/**
 * ErrorBoundary — top-level crash guard.
 *
 * Without this, a single unhandled render/runtime error anywhere in the tree
 * unmounts the whole React app and the user sees a blank white screen with no
 * recovery path. This boundary catches such errors, shows a calm recovery UI,
 * and gives a single place to wire crash reporting (Sentry/PostHog) later.
 *
 * The fallback uses inline styles on purpose: it must render even if the CSS
 * bundle or theme failed to load (a common cause of the crash itself).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/shared/services/telemetry';
import { isChunkLoadError, reloadForFreshChunks } from '@/shared/utils/chunkReload';

interface Props {
    children: ReactNode;
    /** Optional custom fallback renderer. */
    fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        if (isChunkLoadError(error)) {
            // A stale code-split chunk after a deploy — expected churn, not a crash.
            // Auto-recover by reloading (bounded against loops); skip crash telemetry.
            reloadForFreshChunks();
            return;
        }
        // Single funnel for crash telemetry (no-op until a provider is attached).
        reportError(error, { componentStack: info.componentStack, source: 'ErrorBoundary' });
    }

    reset = (): void => {
        this.setState({ error: null });
    };

    render(): ReactNode {
        const { error } = this.state;
        if (!error) return this.props.children;

        if (this.props.fallback) return this.props.fallback(error, this.reset);

        // Stale code-split chunk after a deploy → calm "Updating…" (componentDidCatch
        // has already kicked off a reload) rather than a scary crash screen.
        if (isChunkLoadError(error)) {
            return (
                <div
                    role="status"
                    style={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        padding: 24,
                        background: '#f5f5f4',
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        color: '#57534e',
                    }}
                >
                    <p style={{ margin: 0, fontSize: 15 }}>Updating to the latest version…</p>
                    {/* Through the heal, and forced: a plain location.reload() is
                        answered by the OLD service worker with the same old build,
                        so the button changed nothing (see chunkReload.ts). */}
                    <button
                        onClick={() => reloadForFreshChunks({ force: true })}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 8,
                            border: 'none',
                            background: '#0d9488',
                            color: '#fff',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        Get the latest version
                    </button>
                </div>
            );
        }

        const isDev = import.meta.env.DEV;
        return (
            <div
                role="alert"
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    background: '#f5f5f4',
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
            >
                <div
                    style={{
                        maxWidth: 480,
                        width: '100%',
                        background: '#fff',
                        border: '1px solid #e7e5e4',
                        borderRadius: 12,
                        padding: 32,
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                >
                    {/* Inline SVG (lucide AlertTriangle path) — must render without any CSS/icon bundle */}
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#d97706"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        style={{ display: 'block', margin: '0 auto 12px' }}
                    >
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </svg>
                    <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#1c1917' }}>
                        Something went wrong
                    </h1>
                    <p style={{ margin: '0 0 24px', fontSize: 14, lineHeight: 1.6, color: '#57534e' }}>
                        The page hit an unexpected error. Your data is safe — nothing was lost.
                        Try reloading, and if it keeps happening, contact support.
                    </p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '10px 20px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#0d9488',
                                color: '#fff',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Reload page
                        </button>
                        <button
                            onClick={() => { window.location.href = '/'; }}
                            style={{
                                padding: '10px 20px',
                                borderRadius: 8,
                                border: '1px solid #d6d3d1',
                                background: '#fff',
                                color: '#1c1917',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Go to dashboard
                        </button>
                    </div>
                    {isDev && (
                        <pre
                            style={{
                                marginTop: 20,
                                padding: 12,
                                background: '#fafaf9',
                                border: '1px solid #e7e5e4',
                                borderRadius: 8,
                                fontSize: 12,
                                color: '#b91c1c',
                                textAlign: 'left',
                                overflow: 'auto',
                                maxHeight: 200,
                            }}
                        >
                            {error.message}
                            {'\n'}
                            {error.stack}
                        </pre>
                    )}
                </div>
            </div>
        );
    }
}
